import React, {
  useState, useEffect, useReducer,
} from 'react';
import PropTypes from 'prop-types';
import {
  Tooltip, TooltipButton, IconInfo, Input,
} from '@americanexpress/dls-react';
import useDynamicValidations from '../../CustomHooks/useDynamicValidations';
import {
  parseHtml, convertFullToHalfWidth,
  formatUnFormatString,
} from '../../utils/util';
import { validateDlsInput } from '../../Validations/Validations';
import { getRequiredData } from '../../constants';
import { performOperations } from '../../Operations/Operations';
import { calculatorDlsInput } from '../Calculations/Calculation';

const getVal = require('lodash/get');
/**
 * returns DLS input component
 * @param {*} props
 */
/* eslint complexity: ["error", 40] -- need to increase complexity */
/* eslint-disable max-len -- need */
const DlsInput = (props) => {
  const {
    fieldType,
    fieldLabel,
    fieldPlaceholder,
    fieldId,
    fieldValue,
    fieldValidation,
    filledFormData,
    memberInfo,
    selectKey,
    preFilledWith,
    isPreFilled,
    fetchUrl,
    textWidth,
    isVerifyCard,
    setFormSubmissionPayload,
    formSubmissionPayload,
    validations = [],
    dynamicValidations = [],
    dynamicValidationDeps = ['curPageValues'],
    setValidationErrors,
    validationErrors,
    setFocus,
    fieldRank,
    cssProperties,
    entityToEdit,
    idsToClear,
    curPageValues,
    setCurPageValues,
    operationsToPerform,
    dispatchIdsState,
    extractMapResponseValueByKey,
    comments,
    setComments,
    entityComments,
    entityCompositeKey,
    dataObject,
    inputFormat,
    changePageValues,
    setChangePageValues,
    isChangePageValue = false,
    fieldDynamicRequired = null,
    journeyId,
    iformIntegrationUrls,
    loggerApiCall,
    isSelectivePrefill = false,
    getReviewDisabled,
  } = props;

  const [data, setData] = useState(null);
  const [inValidError, setInvalidError] = useState(false);
  const [customInvalidMsg, setCustomInvalidMsg] = useState('');
  const [showCustomInvalidMsg, setShowCustomInvalidMsg] = useState(false);
  const [status, setStatus] = useState('success');
  const [isValid, setIsValid] = useState(true);
  const [validationErrState, dispatchErrState] = useReducer((prevState, payload) => ({ ...prevState, ...payload }), { validationErr: '', isValid: true });
  const optionalLabel = fieldLabel && Object.prototype.hasOwnProperty.call(fieldLabel, 'optionalLabel') ? fieldLabel.optionalLabel : ' (Optional)';

  const performCustomValidations = (params) => {
    const { inputVal, validationsToPerform } = params;
    const invalidMsg = validateDlsInput(inputVal, validationsToPerform, { ...dataObject, isFieldRequired: fieldValidation.required });
    if (invalidMsg) {
      dispatchErrState({ validationErr: `${invalidMsg}`, isValid: false });
    } else {
      dispatchErrState({ validationErr: '', isValid: true });
    }
  };

  useDynamicValidations({
    data,
    dynamicValidations,
    performCustomValidations,
    dependencyArray: dynamicValidations.length > 0 ? dynamicValidationDeps.reduce(
      (deps, key) => {
        deps.push(getVal(props, key));
        return deps;
      }, [curPageValues[fieldId]])
      : [],
  });

  useEffect(() => {
    // This will clear the mandatory error message for this input
    if (fieldDynamicRequired && fieldValidation && !fieldValidation.required) {
      const mandatoryValidation = validations.find((val) => val.type === 'mandatoryField');
      if (mandatoryValidation) {
        performCustomValidations({ inputVal: data, validationsToPerform: validations });
      }
    }
  }, [fieldValidation.required]);

  /**
   * handle input value
   * @param {*} event
   */
  const handleInput = (event, val = null) => {
    // to protect against performing onChange operations via devtools manipulation, when field is disabled
    //  from fieldValidation or review tool.
    if (getReviewDisabled(fieldId)) return;
    if (comments && comments[fieldId]) {
      // This isValid is for showing red borders for the comments from the review tool.
      setIsValid(false);
      delete comments[fieldId];
      setComments({ ...comments });
    }
    const inputVal = event ? event.target.value : val;

    if (event) {
      setIsValid(true);
    } else if (entityComments.length > 0
      && entityCompositeKey
      && Object.keys(entityComments.filter(
        (obj) => obj.compositeKey === entityCompositeKey)).length > 0
      && Object.keys(entityComments.filter(
        (obj) => obj.compositeKey === entityCompositeKey)[0]).includes(fieldId)) {
      // This isValid is for showing red borders for the comments from the review tool.
      setIsValid(false);
    }

    if (validations.length > 0) {
      setData(inputVal);
      performCustomValidations({ inputVal, validationsToPerform: validations });
    } else {
      setData(inputVal);
      if (!inputVal) setInvalidError(false);
    }
    if (isVerifyCard) {
      if (fieldId === 'cardNumber') {
        const cardNumber = event.target.value;
        setFormSubmissionPayload({
          ...formSubmissionPayload,
          ...{
            elemId: null, cardNumber, cardNameVerify: 1, cardMatchStatus: 1, caseStatusMatch: 1,
          },
        });
      }
      if (fieldId === 'caseNumber') {
        const caseNumber = event.target.value;
        setFormSubmissionPayload({
          ...formSubmissionPayload,
          ...{
            elemId: null, caseNumber, cardNameVerify: 1, cardMatchStatus: 1, caseStatusMatch: 1,
          },
        });
      }
    }
    setCurPageValues({ ...curPageValues, [fieldId]: inputVal });
    if (isChangePageValue) {
      changePageValues[fieldId] = inputVal;
      setChangePageValues({ ...changePageValues });
    }
  };

  useEffect(() => {
    if (filledFormData && filledFormData[fieldId]) {
      setData(filledFormData[fieldId]);
      curPageValues[fieldId] = filledFormData[fieldId];
      setCurPageValues({ ...curPageValues });
      if (isChangePageValue) {
        changePageValues[fieldId] = filledFormData[fieldId];
        setChangePageValues({ ...changePageValues });
      }
    }
  }, [fieldId]);

  useEffect(() => {
    if (isPreFilled && preFilledWith === 'filledFormData' && !fetchUrl) {
      let preFilledVal = '';
      if (getVal(filledFormData, selectKey) === 0) {
        preFilledVal = 0;
      } else if (getVal(filledFormData, selectKey)) {
        preFilledVal = getVal(filledFormData, selectKey);
      }
      setData(preFilledVal);
    } else if (isPreFilled && preFilledWith === 'memberInfo' && !fetchUrl) {
      if (memberInfo) {
        // we have to combine the data from memberInfo for below keys
        const preFilledVal = getRequiredData({
          isPreFilled, preFilledWith, fieldValue, selectKey, fetchUrl, filledFormData, memberInfo,
        });
        setData(preFilledVal);
      }
    } else if (fieldValue !== 'default' && fieldValue !== '') {
      setData(fieldValue);
      setCurPageValues({ ...curPageValues, [fieldId]: fieldValue });
    } else if (fieldValidation?.requiredFields?.length > 0) {
      const processedFields = fieldValidation.requiredFields.map((field) => {
        let dynamicResult = 'false';
        if (field.dynamicOperation) {
          dynamicResult = extractMapResponseValueByKey(field, 'dynamicOperation');
        }
        return { ...field, dynamicResult };
      });
      const preFilledVal = calculatorDlsInput({
        requiredFields: processedFields,
        filledFormData,
      });
      setData(preFilledVal);
    } else if (isChangePageValue && fieldId in changePageValues) {
      setData(getVal(changePageValues, fieldId));
    }
  }, isSelectivePrefill ? [preFilledWith === 'filledFormData'
    ? filledFormData[`${selectKey}`] : getVal(dataObject, preFilledWith)[`${selectKey}`]]
    : [memberInfo, filledFormData, fieldId]);

  useEffect(() => {
    setIsValid(true);
    if (entityToEdit && typeof entityToEdit[fieldId] !== 'undefined') {
      handleInput(null, entityToEdit[fieldId]);
    }
  }, [entityToEdit]);

  useEffect(() => {
    if (comments && comments[fieldId]) {
      setIsValid(false);
    }
  }, [comments]);

  useEffect(() => {
    if (isPreFilled && preFilledWith === 'curPageValues' && !fetchUrl) {
      if (curPageValues && getVal(curPageValues, selectKey) !== data) {
        setData(getVal(curPageValues, selectKey));
      }
    }
  }, [curPageValues]);

  useEffect(() => {
    if (getVal(idsToClear.ids, fieldId)) {
      setData('');
      changePageValues[fieldId] = '';
      setChangePageValues({ ...changePageValues });
      delete idsToClear.ids[fieldId];
      dispatchIdsState({ toggleState: idsToClear.toggleState, ids: idsToClear });
      // if input is cleared, clear corresponding error msg as well
      if (!validationErrState.isValid) {
        dispatchErrState({ validationErr: '', isValid: true });
      }
    }
  }, [idsToClear.toggleState]);

  useEffect(() => {
    // validationErr also can change multiple times, so need one isValid
    if (validationErrState.isValid && validationErrors[fieldRank]) {
      delete validationErrors[fieldRank];
      setValidationErrors({ ...validationErrors });
    } else if (!validationErrState.isValid) {
      setValidationErrors({ ...validationErrors, ...{ [fieldRank]: `${fieldId}` } });
    }
  }, [validationErrState.isValid]);

  useEffect(() => {
    if (isChangePageValue && fieldId in changePageValues) {
      setData(getVal(changePageValues, fieldId));
    }
  }, [changePageValues, isChangePageValue]);

  const fieldValueValidation = (event, value) => {
    if (fieldValidation.isNumeric) {
      // eslint-disable-next-line unicorn/better-regex -- need
      if (!/^[0-9]+$/.test(value)) {
        event.preventDefault();
      }
    } else if (fieldValidation.isAlphabet) {
      const re = /^[A-Za-z]+$/;
      if (!re.test(value)) {
        event.preventDefault();
      }
    } else if (fieldValidation.isAlphaNumeric) {
      const alphaNu = /^[!"#$%&'()*+,./:;<=>?@[\\\]^_{|}]*$/;
      if (alphaNu.test(value)) {
        event.preventDefault();
      }
    } else if (fieldValidation.allowedKeysRegex) {
      const rex = new RegExp(fieldValidation.allowedKeysRegex);
      if (!rex.test(value)) {
        event.preventDefault();
      }
    }
  };

  const handlePaste = (event) => {
    const pasteText = event.clipboardData.getData('text');
    fieldValueValidation(event, pasteText);
  };

  const handleKeyPress = (event) => {
    fieldValueValidation(event, event.key);
  };

  const handleValidation = (e) => {
    const str = e.target.value;
    /* eslint-disable-next-line no-useless-escape -- required */
    const re = /^[\d/a-z\-]+$/i;
    if (!re.test(str)) {
      setInvalidError(true);
    } else if (str.length < fieldValidation.minSize) {
      setInvalidError(true);
    } else if (str.length < fieldValidation.maxSize) {
      setInvalidError(true);
    } else {
      setInvalidError(false);
    }

    // To add comma
    if (fieldValidation.isNumberFormatRequired) {
      const formattedValue = (Number(str.replace(/\D/g, '')) || '').toLocaleString();
      setData(formattedValue);
    }

    // convert double byte integer to single byte
    if (fieldValidation.isSingleToDoubleByteConversion) {
      const newStr = str.replace(/ /g, '');
      setData(newStr);
    }
  };

  const invalidHandler = (e) => {
    if (validations.length > 0 || dynamicValidations.length > 0) {
      e.preventDefault();
      const validationsToPerform = validations.length > 0 ? validations : dynamicValidations;
      performCustomValidations({ inputVal: null, validationsToPerform });
      setFocus({ ...validationErrors, ...{ [fieldRank]: `${fieldId}` } });
    } else if (fieldValidation && fieldValidation.hideDefaultValidationMsg) {
      e.preventDefault();
      document.querySelector(`#${fieldId}`).focus();
      if (!e.target.value) {
        setCustomInvalidMsg('Please complete this field');
      } else {
        setCustomInvalidMsg(fieldValidation
          && fieldValidation.customInvalidMsg
          ? fieldValidation.customInvalidMsg : 'Please fill in this field');
      }
      setShowCustomInvalidMsg(true);
      setStatus('error');
    } else {
      e.target.setCustomValidity(`${customInvalidMsg}`);
    }
    // invoke some error message red color
  };
  const onInputHandler = (e) => {
    e.target.setCustomValidity('');
    setShowCustomInvalidMsg(false);
    setStatus('success');
  };

  const handleOnBlur = (e) => {
    // to protect against performing onBlur operations via devtools manipulation, when field is disabled
    //  from fieldValidation or review tool.
    if (fieldValidation.disabled || getReviewDisabled(fieldId)) return;
    if (operationsToPerform?.onBlur?.length > 0 && validationErrState.isValid) {
      performOperations({
        value: e.target.value,
        operations: operationsToPerform.onBlur,
        dataObject: {
          memberInfo, formSubmissionPayload, curPageValues, journeyId, iformIntegrationUrls,
        },
        functionObject: { setFormSubmissionPayload, loggerApiCall },
        setFormSubmissionPayload,
      });
    }
    if (e?.target?.value) {
      setData(() => e.target.value.trim());
    }
    if (inputFormat && e.target.value) {
      setData((prev) => formatUnFormatString(prev, inputFormat, true));
    }
  };

  const handleOnFocus = (e) => {
    if (inputFormat && e.target.value) {
      setData(formatUnFormatString(e.target.value, inputFormat, false));
    }
  };

  const renderElement = (
    <Input
      status={status}
      // This isValid is for showing red borders for the comments from the review tool.
      // No input field should show comments if it does not have any data.(Entity page)
      style={{
        border: !isValid && data ? '.0625rem solid red' : '.0625rem solid #8e9092',
      }}
      type={fieldValidation && fieldValidation.type ? fieldValidation.type : fieldType}
      id={fieldId}
      key={fieldId}
      name={fieldId}
      pattern={fieldValidation && fieldValidation.pattern ? fieldValidation.pattern : '*'}
      // className=""
      value={data && textWidth ? convertFullToHalfWidth(data) : data}
      placeholder={fieldPlaceholder}
      onChange={handleInput}
      disabled={fieldValidation.disabled || getReviewDisabled(fieldId)}
      required={fieldValidation.required}
      maxLength={fieldValidation.maxSize}
      minLength={fieldValidation.minSize}
      onKeyPress={handleKeyPress}
      onPaste={handlePaste}
      onKeyUp={handleValidation}
      aria-describedby="validFieldAlert"
      title=""
      onInvalid={invalidHandler}
      onInput={onInputHandler}
      onBlur={handleOnBlur}
      onFocus={handleOnFocus}
    />
  );

  return (
    <>
      {
        fieldLabel && fieldLabel.label && (
          <label className={fieldLabel.cssProperties.style} htmlFor={fieldId}>
            {parseHtml(fieldLabel.label)}
          </label>
        )
      }
      {!fieldValidation.required && optionalLabel && (
        <span
          className={fieldLabel.cssProperties.style}
        >
          {optionalLabel}
        </span>
      )}
      {fieldValidation && fieldValidation.isToolTip
        && (
          <>
            <span>{' '}</span>
            <Tooltip id="ToolTip" style={fieldValidation.toolTipStyles ? fieldValidation.toolTipStyles : {}} triggerElement={<TooltipButton icon={<IconInfo size="sm" title="info" />} />}>{parseHtml(fieldValidation.toolTipInfo)}</Tooltip>
          </>
        )}
      {fieldLabel
        && fieldLabel.subLabel
        && (
          <div>
            <label className={fieldLabel.cssProperties.subLabelStyle} htmlFor={fieldId}>
              {parseHtml(fieldLabel.subLabel)}
            </label>
          </div>
        )}
      {renderElement}
      {fieldValidation.disabled && (
        <input type="hidden" id={fieldId} name={fieldId} value={data} />
      )}
      {inValidError && (
        <div id="validFieldAlert" className="alert-form flex" role="alert">
          <i className="valign-top margin-1-r" data-dls-icon="warning" data-dls-icon-variant="filled" data-dls-icon-size="sm" data-dls-icon-role="decorative" />
          <span>{parseHtml(fieldValidation.error)}</span>
        </div>
      )}
      {showCustomInvalidMsg && (
        <div id="alert-error" className="alert dls-color-warning pad-1-t">
          <span className="icon icon-sm dls-icon-warning-filled pad-1-r" />
          <span>{customInvalidMsg}</span>
        </div>
      )}
      {
        validationErrState.validationErr
        && (
          <div id="alert-error" className={cssProperties.validationErrorStyle ? cssProperties.validationErrorStyle : 'alert dls-color-warning pad-1-t col-md-12'}>
            <span className="icon icon-sm dls-icon-warning-filled pad-1-r" />
            <span>{parseHtml(validationErrState.validationErr)}</span>
          </div>
        )
      }
      {
        comments && comments[fieldId]
        && (
          <div id="alert-error" className="alert dls-color-warning pad-1-t">
            <span className="icon icon-sm dls-icon-warning-filled pad-1-r" />
            <span>{comments[fieldId].comment}</span>
          </div>
        )
      }
      {
        entityComments.length > 0
        && entityCompositeKey
        && Object.keys(entityComments.filter(
          (obj) => obj.compositeKey === entityCompositeKey)).length > 0
        && Object.keys(entityComments.filter(
          (obj) => obj.compositeKey === entityCompositeKey)[0]).includes(fieldId)
        && !isValid
        && (
          <div id="alert-error" className="alert dls-color-warning pad-1-t">
            <span className="icon icon-sm dls-icon-warning-filled pad-1-r" />
            <span>{
              fieldId === 'compositeKey' ? entityComments.filter((obj) => obj.compositeKey === entityCompositeKey)[0][fieldId]
                : entityComments.filter(
                  (obj) => obj.compositeKey === entityCompositeKey)[0][fieldId].defaultValue
            }
            </span>
          </div>
        )
      }
    </>
  );
};
/* eslint-enable max-len -- need */
DlsInput.propTypes = {
  fieldType: PropTypes.string,
  fieldLabel: PropTypes.objectOf(PropTypes.shape),
  fieldPlaceholder: PropTypes.string,
  fieldId: PropTypes.string,
  fieldValue: PropTypes.string,
  fieldValidation: PropTypes.objectOf(PropTypes.shape),
  preFilledWith: PropTypes.string,
  fetchUrl: PropTypes.string,
  filledFormData: PropTypes.objectOf(PropTypes.shape),
  memberInfo: PropTypes.objectOf(PropTypes.shape),
  selectKey: PropTypes.string,
  isPreFilled: PropTypes.bool,
  textWidth: PropTypes.string,
  isVerifyCard: PropTypes.bool,
  setFormSubmissionPayload: PropTypes.func,
  formSubmissionPayload: PropTypes.objectOf(PropTypes.shape),
  setValidationErrors: PropTypes.func,
  validationErrors: PropTypes.objectOf(PropTypes.shape),
  validations: PropTypes.arrayOf(PropTypes.shape),
  setFocus: PropTypes.func,
  fieldRank: PropTypes.number,
  idsToClear: PropTypes.objectOf(PropTypes.shape),
  entityToEdit: PropTypes.objectOf(PropTypes.shape),
  cssProperties: PropTypes.objectOf(PropTypes.shape),
  curPageValues: PropTypes.objectOf(PropTypes.shape),
  setCurPageValues: PropTypes.func,
  comments: PropTypes.objectOf(PropTypes.shape),
  setComments: PropTypes.func,
  entityComments: PropTypes.objectOf(PropTypes.shape),
  entityCompositeKey: PropTypes.string,
  dynamicValidations: PropTypes.arrayOf(PropTypes.shape),
  operationsToPerform: PropTypes.objectOf(PropTypes.shape),
  dispatchIdsState: PropTypes.func,
  dynamicValidationDeps: PropTypes.arrayOf(PropTypes.string),
  extractMapResponseValueByKey: PropTypes.func,
  dataObject: PropTypes.objectOf(PropTypes.shape),
  inputFormat: PropTypes.string,
  changePageValues: PropTypes.objectOf(PropTypes.shape),
  setChangePageValues: PropTypes.func,
  isChangePageValue: PropTypes.bool,
  fieldDynamicRequired: PropTypes.objectOf(PropTypes.shape),
  journeyId: PropTypes.string,
  iformIntegrationUrls: PropTypes.string,
  loggerApiCall: PropTypes.func,
  isSelectivePrefill: PropTypes.bool,
  getReviewDisabled: PropTypes.func,
};

export default DlsInput;
